// AuthGate — route guard a nivel root. Decide entre:
//   - status='loading'  → spinner full screen mientras AuthProvider
//                          resuelve sesión.
//   - status='anonymous' Y ruta privada → redirect a /login (con
//                          deep-link preservado para post-login).
//   - status='authenticated' Y ruta es /login → redirect a home (resolved
//                          via userRole).
//   - default → render children (RouterProvider).
//
// Rutas públicas: /login, /signup-*, /forgot-password, /reset-password,
// /auth/callback. Cualquier otra ruta requiere sesión.
//
// Sub-decisión: usamos useLocation + window.location.replace para
// redirects que ocurren ANTES de que el router maneje la ruta. Esto
// evita race conditions con la navegación interna del router.

import { useEffect } from "react";
import { Loader2 } from "lucide-react";
import { useAuth } from "./AuthProvider";

const PUBLIC_ROUTES: readonly string[] = [
  "/login",
  "/forgot-password",
  "/reset-password",
  "/auth/callback",
  "/signup-kine",
  "/signup-kine/email-enviado",
  "/signup-paciente",
  "/signup-paciente/email-enviado",
  "/signup-paciente/error",
];

function isPublicRoute(pathname: string): boolean {
  return PUBLIC_ROUTES.includes(pathname);
}

interface Props {
  children: React.ReactNode;
}

export function AuthGate({ children }: Props) {
  const { status, userRole } = useAuth();
  const pathname = typeof window !== "undefined" ? window.location.pathname : "";

  useEffect(() => {
    if (status === "loading") return;

    if (status === "anonymous") {
      if (!isPublicRoute(pathname)) {
        // Preservamos el path original como query param `next` para que
        // Login pueda redirigir post-login. Si es "/" o "/login" no aporta.
        const next =
          pathname && pathname !== "/"
            ? `?next=${encodeURIComponent(pathname)}`
            : "";
        window.location.replace(`/login${next}`);
      }
      return;
    }

    // status === 'authenticated'
    // Si aterrizó en /login (manual o bookmark), mandalo a su home según rol.
    if (pathname === "/login") {
      if (userRole === "kine") window.location.replace("/");
      else if (userRole === "paciente") window.location.replace("/paciente-home");
      // userRole undefined/null → lookup pending o huérfano. No redirect —
      // dejamos al user en /login mientras AuthProvider resuelve.
      return;
    }

    // Si es paciente y está en "/" (home del kine), mandalo a su home.
    if (pathname === "/" && userRole === "paciente") {
      window.location.replace("/paciente-home");
    }
  }, [status, userRole, pathname]);

  // Spinner full-screen mientras AuthProvider carga sesión inicial.
  // Sin esto, el RouterProvider monta los routes y los hooks disparan
  // con userId=null antes de que sepamos si hay sesión cacheada.
  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Si está anonymous en ruta privada, el useEffect ya disparó el
  // window.location.replace — renderea un blank mientras el browser navega.
  if (status === "anonymous" && !isPublicRoute(pathname)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return <>{children}</>;
}
